import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

export default function CourtTemplateCreatePage() {
  const navigate = useNavigate();
  const { courtTypeId } = useParams<{ courtTypeId: string }>();
  const [name, setName] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [courtTypeName, setCourtTypeName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load court type name for display
  useEffect(() => {
    api
      .getCourtTypes()
      .then((res) => {
        const ct = res.court_types.find((t) => t.id === courtTypeId);
        if (ct) setCourtTypeName(ct.name);
      })
      .catch(() => {});
  }, [courtTypeId]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!courtTypeId) return;
    setError(null);
    setSubmitting(true);

    try {
      const res = await api.createCourtTemplate(courtTypeId, {
        name,
        is_default: isDefault || undefined,
      });
      navigate(`/court-templates/${res.template.id}`);
    } catch (err) {
      setError(
        err instanceof api.ApiError
          ? err.message
          : "Failed to create template",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-page="court-template-create" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Add Court Template</h1>
        <p className="text-muted-foreground">
          Create a new mount layout template
          {courtTypeName ? ` for ${courtTypeName}` : ""}.
        </p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">Template Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-form="court-template-create">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                data-field="name"
                placeholder="e.g. Standard Rugby Field"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                id="is-default"
                data-field="is-default"
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="h-4 w-4 rounded border border-input"
              />
              <Label htmlFor="is-default">
                Set as default template for this court type
              </Label>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting}>
                {submitting ? "Creating..." : "Create Template"}
              </Button>
              <Link
                to="/court-templates"
                className={buttonVariants({ variant: "outline" })}
                data-action="cancel"
              >
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
